import { IPublicClientApplication } from '@azure/msal-browser'
import {
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchLocationReports,
  updateReportStatus,
} from '../../data/admin.service'
import { LocationReport } from '../../models/Admin/LocationReport'

interface LocationReportsProps {
  instance: IPublicClientApplication
}

const LocationReports = ({ instance }: LocationReportsProps) => {
  const navigate = useNavigate()
  const [reports, setReports] = useState<LocationReport[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)

  const loadReports = useCallback(async () => {
    setIsLoading(true)
    try {
      const fetchedReports = await fetchLocationReports(
        instance,
        selectedStatus,
      )
      setReports(fetchedReports)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load location reports. Please try again later.',
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }, [instance, selectedStatus])

  useEffect(() => {
    loadReports()
  }, [loadReports])

  const handleStatusUpdate = async (reportId: number, newStatus: string) => {
    setUpdatingId(reportId)
    try {
      await updateReportStatus(reportId, newStatus, instance)
      notifications.show({
        title: 'Success',
        message: 'Report status updated successfully.',
        color: 'green',
      })
      loadReports() // Reload reports to get updated status
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update report status. Please try again later.',
        color: 'red',
      })
    } finally {
      setUpdatingId(null)
    }
  }

  const LoadingSkeleton = () => (
    <Stack p={32}>
      {[1, 2, 3].map((i) => (
        <Paper key={i} p='md' radius='md' withBorder>
          <Group justify='space-between' mb='xs'>
            <Skeleton height={24} width={200} />
            <Skeleton height={24} width={100} />
          </Group>
          <Stack gap='xs'>
            <Skeleton height={16} width={150} />
            <Skeleton height={16} width={200} />
            <Skeleton height={16} width={100} />
            <Skeleton height={60} width='100%' />
            <Group mt='md' justify='flex-end'>
              <Skeleton height={36} width={120} />
            </Group>
          </Stack>
        </Paper>
      ))}
    </Stack>
  )

  return (
    <Stack p={32}>
      <Group justify='space-between'>
        <Title order={2}>Location Reports</Title>
        <Select
          label='Filter by Status'
          placeholder='All Statuses'
          value={selectedStatus}
          onChange={setSelectedStatus}
          data={[
            { value: '', label: 'All Statuses' },
            { value: 'Reported', label: 'Reported' },
            { value: 'Ignored', label: 'Ignored' },
            { value: 'Changed', label: 'Changed' },
          ]}
          clearable
        />
      </Group>

      {isLoading ? (
        <LoadingSkeleton />
      ) : reports.length === 0 ? (
        <Text>No reports found.</Text>
      ) : (
        reports.map((report) => (
          <Paper key={report.id} p='md' radius='md' withBorder>
            <Group justify='space-between' mb='xs'>
              <UnstyledButton
                onClick={() => navigate(`/locations/${report.locationId}`)}
              >
                <Title
                  order={4}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {report.locationName}
                </Title>
              </UnstyledButton>
              <Badge
                color={
                  report.status === 'Ignored'
                    ? 'gray'
                    : report.status === 'Changed'
                    ? 'green'
                    : 'red'
                }
              >
                {report.status}
              </Badge>
            </Group>

            <Stack gap='xs'>
              <Text size='sm' c='dimmed'>
                Reported on {new Date(report.reportedDate).toLocaleDateString()}
              </Text>

              <Text size='sm'>
                <strong>Reported by:</strong> {report.userEmail}
              </Text>

              <Text size='sm'>
                <strong>Report Reason: </strong>
                {report.reason}
              </Text>

              {report.status === 'Reported' && (
                <Group mt='md' justify='flex-end'>
                  <Button
                    variant='light'
                    color='gray'
                    onClick={() => handleStatusUpdate(report.id, 'Ignored')}
                    loading={updatingId === report.id}
                    disabled={updatingId === report.id}
                    loaderProps={{ type: 'dots' }}
                  >
                    Mark as Ignored
                  </Button>
                  <Button
                    variant='light'
                    color='blue'
                    onClick={() => handleStatusUpdate(report.id, 'Changed')}
                    loading={updatingId === report.id}
                    disabled={updatingId === report.id}
                    loaderProps={{ type: 'dots' }}
                  >
                    Mark as Changed
                  </Button>
                </Group>
              )}
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  )
}

export default LocationReports
